import { defineAsyncComponent, markRaw } from 'vue'
import { createApp, type ModuleConfig } from '@bridge2ai/core/frontend'
import config from './config/modules.config.json'
import terminology from '../../terminology.json'
import EmsImportWindow from './components/EmsImportWindow.vue'

// Tenant-local modules (see frontend/src/modules/usage-monitor,
// modules/client-cost-report and modules/demo-datasets).
const customModules: ModuleConfig[] = [
  {
    id: 'demoDatasets',
    moduleId: 'demo-datasets',
    slug: 'demo-datasets',
    name: 'Demo Datasets',
    iconUrl: '/icons/demo-datasets.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/demo-datasets/DemoDatasetsApp.vue')),
  },
  {
    id: 'costReport',
    moduleId: 'cost-report',
    slug: 'cost-report',
    name: 'Cost Report',
    iconUrl: '/icons/cost-report.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/client-cost-report/ClientCostReport.vue')),
  },
  // Client Access Portal (demo): RFQ drop zone, the plant's produced products
  // and a rush-order check against Core's scheduler dry run. Internal id and
  // URL keep the original rush-orders name. Shares the scheduler's
  // permission id, so it is visible to exactly the groups that see the
  // Production Scheduler.
  {
    id: 'rushOrders',
    moduleId: 'production-scheduler',
    slug: 'rush-orders',
    name: 'Client Access Portal',
    iconUrl: '/icons/rush-orders.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/rush-orders/RushOrdersApp.vue')),
  },
  // F-Track Demo: phone-scanning dashboards + QR sheet generator (CouchDB-
  // only, works offline against the local btrack DB).
  {
    id: 'btrackActuals',
    moduleId: 'btrack-report',
    slug: 'btrack-actuals',
    name: 'F-Track Demo',
    iconUrl: '/icons/ftrack.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/btrack-actuals/BtrackActuals.vue')),
  },
  {
    id: 'btrackBarcodes',
    moduleId: 'btrack-barcodes',
    slug: 'btrack-barcodes',
    name: 'F-Track Barcodes',
    iconUrl: '/icons/btrack-barcodes.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/btrack-barcodes/BtrackBarcodes.vue')),
  },
  {
    id: 'usageMonitor',
    moduleId: 'usage-monitor',
    slug: 'usage-monitor',
    name: 'Usage Monitor',
    iconUrl: '/icons/usage-monitor.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/usage-monitor/UsageMonitorApp.vue')),
  },
  // Outreach (ported from the ~/github/Internal/crm Core fork): two modules
  // sharing one folder and one report kit (modules/outreach/shared).
  {
    id: 'crm',
    moduleId: 'crm',
    slug: 'crm',
    name: 'CRM',
    iconUrl: '/icons/crm.svg',
    // Both outreach apps render their own header bar; suppress Core's.
    showGlobalHeader: false,
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/outreach/crm/components/CrmApp.vue')),
  },
  {
    id: 'eventResearch',
    moduleId: 'event-research',
    slug: 'events',
    name: 'Events',
    iconUrl: '/icons/events.svg',
    showGlobalHeader: false,
    componentLoader: () =>
      defineAsyncComponent(() =>
        import('./modules/outreach/event-research/components/EventResearchApp.vue')),
  },
  // Prospects: daily manufacturer lists (approve/deny + trigger events with key people), researched
  // each morning by the desktop routine in scripts/prospects/.
  {
    id: 'prospects',
    moduleId: 'prospects',
    slug: 'prospects',
    name: 'Prospects',
    iconUrl: '/icons/prospects.svg',
    showGlobalHeader: false,
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/prospects/ProspectsApp.vue')),
  },
  // Branches: swim lanes of Core + client repo branches with each environment pinned on the
  // branch it runs (snapshot from bridge-mes's branch-board workflow).
  {
    id: 'branchBoard',
    moduleId: 'branch-board',
    slug: 'branches',
    name: 'Branches',
    iconUrl: '/icons/branches.svg',
    componentLoader: () =>
      defineAsyncComponent(() => import('./modules/branch-board/BranchBoard.vue')),
  },
]

createApp({
  tenantId: 'bridge-internal',
  config: config as any,
  customModules,
  // Tenant UI pack: 1EMS vocabulary + the EMS fields the scheduler sync
  // attaches under ext.* — surfaced as first-class, filterable columns.
  tenant: {
    apiVersion: 1,
    terminology: terminology as any,
    // The Import Data modal content is tenant-owned: EmsImportWindow drives
    // the manual 1EMS/Odoo sync + optimizer (replaces Core's Epicor-shaped
    // default window entirely, so no sync label/endpoint config is needed).
    components: {
      importWindow: markRaw(EmsImportWindow),
    },
    fieldLabels: {
      jobNum: 'MO',
      soId: 'PO',
    },
    fields: [
      { key: 'extManager', source: 'ext.manager', label: 'Manager', entity: 'job', type: 'enum' },
      { key: 'extPjtCode', source: 'ext.pjtCode', label: 'Project', entity: 'job', type: 'string' },
      { key: 'extCustPo', source: 'ext.custPo', label: 'Customer PO#', entity: 'job', type: 'string' },
      { key: 'extTitle', source: 'ext.title', label: 'MO Title', entity: 'job', type: 'string' },
    ],
    // Material-tracking fields — BridgeInternal has no materials source (no QOH /
    // time-phase feed), so these would render as permanently-empty columns.
    hiddenFields: ['rfeh', 'partsSummary', 'materialText', 'numLines', 'earliestStart'],
    // Managed dashboard views: definitions live HERE, re-applied on every
    // load. "All MOs" is the immutable default; users can add their own
    // views next to these.
    views: [
      {
        name: 'All MOs',
        isDefault: true,
        color: 'slate-blue',
        columns: ['jobNum', 'extTitle', 'extPjtCode', 'extManager', 'extCustPo', 'currentStart', 'issues'],
        sorts: [{ field: 'daysLate', dir: 'desc' }],
        detailFields: ['partNumber', 'estimatedHours', 'salesOrderShipDate'],
      },
      {
        name: 'Issues',
        color: 'clay',
        columns: ['jobNum', 'extTitle', 'extPjtCode', 'currentStart', 'issues'],
        filters: [{ field: 'hasAnyIssue', op: 'is', values: [true] }],
        sorts: [{ field: 'daysLate', dir: 'desc' }],
        detailFields: ['partNumber', 'extManager', 'salesOrderShipDate'],
      },
      {
        name: 'Customer POs',
        list: 'salesOrders',
        color: 'sage',
        columns: ['jobNum', 'extTitle', 'currentStart', 'issues'],
        detailFields: ['partNumber', 'extPjtCode'],
      },
    ],
  },
})
